/**
 * Majority Judgment Leaderboard
 * 
 * Displays ALL active models, including those with < 3 judgments.
 * Unqualified models are dimmed.
 */

import React, { useMemo } from 'react';
import { ModelChip } from '../chat/ModelChip';
import { MajorityJudgment, compareMajorityJudgments } from '../../services/majorityJudgmentService';

interface ClusteredModel {
  modelId: string;
  displayName: string;
  majorityJudgment: MajorityJudgment | null; // Null for unjudged models
  backgroundColor: string;
  isQualified: boolean; // True if meets minimum judgment threshold
}

// Union type for judged data (can be model-level or creator-level)
type JudgedModelData = { modelId: string; userJudgment: MajorityJudgment | null; globalJudgment: MajorityJudgment | null; lastUpdated: Date | null };
type JudgedCreatorData = { creatorPrefix: string; userJudgment: MajorityJudgment | null; globalJudgment: MajorityJudgment | null; lastUpdated: Date | null };

interface MajorityJudgmentLeaderboardProps {
  title?: string; // Optional title for the leaderboard
  onModelClick?: (modelId: string) => void;
  tagColors: string[]; // Colors to assign to models (fallback)
  modelColorAssignments: Map<string, string>; // Map of modelId -> color hex for active models
  activeModels: string[]; // Model IDs that are currently active in the header
  judgedModels: Array<JudgedModelData | JudgedCreatorData>; // Externally provided judged models or creators
  viewMode?: 'creator' | 'model'; // Display mode
  minJudgments: number; // Minimum threshold for qualification (passed from parent)
  prioritySetting?: 'free' | 'speed' | 'balance' | 'value' | 'intelligence' | 'custom'; // Current priority setting
  currentUserId?: string; // NEW: Current user ID for "you" display
  maxChips?: number | null;  // Maximum chips to display (null = show all)
}

export const MajorityJudgmentLeaderboard: React.FC<MajorityJudgmentLeaderboardProps> = ({
  title,
  onModelClick,
  tagColors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'],
  modelColorAssignments = new Map(),
  activeModels = [],
  judgedModels = [],
  viewMode = 'model',
  minJudgments,
  prioritySetting,
  currentUserId,
  maxChips = null,  // Default to showing all
}) => {
  // Use threshold passed from parent (different for conversation/user/global levels)

  // Prepare models for display
  const clusteredModels: ClusteredModel[] = useMemo(() => {
    // Helper to get ID from judged data (handles both model and creator types)
    const getIdFromJudgedData = (item: JudgedModelData | JudgedCreatorData): string => {
      if ('modelId' in item) return item.modelId;
      if ('creatorPrefix' in item) return item.creatorPrefix;
      return 'unknown';
    };
    
    // Create a map of judged items for quick lookup
    const judgedModelsMap = new Map(judgedModels.map(m => [getIdFromJudgedData(m), m]));
    
    // In creator mode, only show judged creators (no active models)
    // In model mode, combine active models with judged models
    const allModelsToShow = viewMode === 'creator' 
      ? new Set(judgedModels.map(m => getIdFromJudgedData(m)))
      : new Set([...activeModels, ...judgedModels.map(m => getIdFromJudgedData(m))]);
    
    const result: ClusteredModel[] = [];
    
    // Process each model
    Array.from(allModelsToShow).forEach((modelId, index) => {
      const judgedModel = judgedModelsMap.get(modelId);
      // Use userJudgment if available (Levels 1 & 2), otherwise globalJudgment (Level 3)
      const mj = judgedModel?.userJudgment || judgedModel?.globalJudgment || null;
      // For global judgments, filter by promptsNumber (prompts). For user/conversation, filter by judgmentsNumber (judgments)
      const isGlobal = !judgedModel?.userJudgment && judgedModel?.globalJudgment;
      const judgmentCount = mj ? (isGlobal ? (mj.promptsNumber ?? 0) : (mj.judgmentsNumber ?? 0)) : 0;
      
      // Skip entries with 0 judgments entirely
      if (judgmentCount === 0) {
        return;
      }
      
      const isQualified = judgmentCount >= minJudgments;
      
      // Check if this model/creator is currently active in the header
      let isActive: boolean;
      let backgroundColor: string;
      
      // Special handling for +human
      if (modelId === '+human') {
        isActive = false; // +human is never "active" in the header
        backgroundColor = '#22c55e'; // green-500 for human
      } else if (viewMode === 'creator') {
        // In creator mode, check if any active model belongs to this creator
        const creatorPrefix = modelId; // modelId is actually creatorPrefix in creator mode
        const activeModelsForCreator = activeModels.filter(m => m.startsWith(`${creatorPrefix}/`));
        isActive = activeModelsForCreator.length > 0;
        
        if (isActive && activeModelsForCreator.length > 0) {
          // Use the color of the first active model from this creator
          const firstActiveModel = activeModelsForCreator[0];
          backgroundColor = modelColorAssignments.get(firstActiveModel) || tagColors[index % tagColors.length];
        } else {
          backgroundColor = '#9ca3af'; // Gray for inactive creators
        }
      } else {
        // In model mode, direct lookup
        // Strip reasoning suffixes (high, low) when comparing
        const baseModelId = modelId.replace(/ \((high|low)\)$/, '');
        isActive = activeModels.includes(modelId) || activeModels.includes(baseModelId);
        if (isActive) {
          // Try to get color from either the full ID or base ID
          backgroundColor = modelColorAssignments.get(modelId) || modelColorAssignments.get(baseModelId) || tagColors[index % tagColors.length];
        } else {
          backgroundColor = '#9ca3af'; // Gray for inactive models
        }
      }
      
      // Display name based on view mode
      // Replace user ID with "+you" if it matches the current user
      // Replace "+human" modelId with "+human" display (global consolidated human responses)
      // Keep lowercase to match header styling throughout the interface
      let displayName = modelId;
      if (modelId === '+human') {
        displayName = '+human';
      } else if (currentUserId && modelId === currentUserId) {
        displayName = '+you';
      }

      result.push({
        modelId,
        displayName,
        majorityJudgment: mj,
        backgroundColor,
        isQualified,
      });
    });

    // Filter to only show qualified models, then sort by judgment
    const qualifiedResults = result.filter(m => m.isQualified);
    
    qualifiedResults.sort((a, b) => {
      // Use proper Balinski gauge algorithm for sorting
      if (a.majorityJudgment && b.majorityJudgment) {
        return compareMajorityJudgments(a.majorityJudgment, b.majorityJudgment);
      }
      return 0;
    });

    return qualifiedResults;
  }, [judgedModels, tagColors, modelColorAssignments, activeModels, viewMode, minJudgments, prioritySetting]);

  // If no qualified models meet the threshold, don't render anything
  if (clusteredModels.length === 0) {
    return null;
  }

  // Apply chip limit if specified
  const visibleModels = maxChips !== null ? clusteredModels.slice(0, maxChips) : clusteredModels;

  return (
    <div className="space-y-1">
      {/* Optional title */}
      {title && (
        <div className="text-center text-xs font-medium text-muted-foreground px-3 py-1">
          {title}
        </div>
      )}
      <div className="px-3 space-y-0.5">
        {visibleModels.map((model) => (
          <ModelChip
            key={model.modelId}
            modelId={model.modelId}
            displayName={model.displayName}
            isActive={false}
            backgroundColor={model.backgroundColor}
            onClick={() => {}}
            className="w-full justify-center text-center"
            majorityJudgment={model.majorityJudgment ? {
              userJudgment: model.majorityJudgment,
              globalJudgment: null
            } : null}
            tagColors={tagColors}
            showFullId={viewMode === 'model'}
            interactive={false}
          />
      ))}
      </div>
    </div>
  );
};


